import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Documents } from 'src/app/models/documents.models';
import { OrderErrors, Orders } from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * StoricoOrdiniDettagliComponent — modale principale dettagli di un
 * ordine chiuso (lanciato da `<app-storico-ordini>` H.1 su row click).
 *
 * Aggrega tutte le informazioni di un ordine evaso:
 *   • info ordine (n°, date, cliente, indirizzo, operatore, corriere)
 *   • prodotti (delegato a `<app-product-details-storico>` H.5)
 *   • colli raggruppati per tipo
 *   • documenti allegati
 *   • etichette PDF (Scatola + Corriere + Borderò + DDT)
 *   • errori registrati (con add/modify/delete inline)
 *
 * Sub-modali aperti dalle action buttons:
 *   • `<app-product-detail-modal>` — dettagli prodotto (storicoOn)
 *   • `<app-errori>` (H.3) — aggiungi nuovo errore
 *   • `<app-modify-error>` (H.4) — modifica errore esistente
 *
 * Phase 1.5 / v2.9.0 — TS cleaned up (Chunk H.2):
 *   • dropped kendo-dialog + ~15 kendo-label + 3× kendo-panelbar +
 *     3× kendo-panelbar-item + ~10 kendoTextBox + kendo-grid errori +
 *     5× kendo-grid-column + kendo-accordion + kendo-dropdownlist
 *     (58 kendo refs → 0 in HTML),
 *
 *   • dropped ~30 console.log/debug ("Esti boss"/"Nu esti boss"
 *     romanian 🇷🇴, "BOSS", "Zeu", "SUCCESS!", "Sunt distrus",
 *     "Barcode caca", "Expiration date is above the starting date!
 *     Man you are crazy man!"),
 *
 *   • dropped ~20 dead state fields: `goBack`, `dataSaved`,
 *     `isEditable`, `maxlength`, `minWidth`, `dialogWidth`,
 *     `dialogHeight`, `product: Products.Product[]` (mai aggiornato
 *     dopo subscribe), `or_url_box_label`, `pr_code`, `clickLabel`,
 *     `errori: Errors`, `selectedErrorType`, `selectedErrorDescription`,
 *     `values`, `ettArray` (factory pattern con 3 onClick mai
 *     usati), `wideviewProducts`, `urlServer` hardcoded,
 *     `errorChange`, `clickedProduct`, `linkBordero`,
 *     `ordersDetailsForm` (form group inutilizzato),
 *
 *   • dropped 12 handler dead: `seeWideViewProducts`,
 *     `toggleAccordion`/`onAccordionChange` (vecchio pattern ionic),
 *     `productDetails`, `openProducts` (modalCtrl Ionic mai
 *     chiamato), `triggerFunction`, `printDDT/Carrier/Bordero` (DOM
 *     anchor click hack mai chiamati dal template), `setErrors`
 *     (vecchia API), `valueChange/selectionChange`, `deleteError2`
 *     (duplicato di deleteError, vecchia versione),
 *     `showSwalSuccess`/`showSwalError`/`showAlertSuccess`,
 *     `seeQrCodes`/`hasValidBarcodes` (duplicati di H.5),
 *     `goEditFunction`, `convertToISODate` (regex date parsing
 *     fragile sostituito con safe Date parsing),
 *
 *   • dropped AlertController + PopoverController + ModalCtrl
 *     Ionic injections (usati solo da handler dead),
 *
 *   🚨 FIX BUG latente — feature gating: il legacy aveva
 *   `environment === urlServer` con `urlServer` hardcoded a
 *   `'http://diakonix.connecta.ovh:88/api/services'` che
 *   gate-controllava intere sezioni (errori). In prod
 *   `environment.urlServer` non corrisponde a quel valore →
 *   **la sezione errori non si attivava mai in produzione**.
 *   Check rimosso, sezione sempre visibile.
 *
 *   🚨 FIX BUG logico: `groupBoxes()` era chiamato in ngOnInit
 *   PRIMA di avere `this.box` (popolato dopo subscribe getOrder1)
 *   → primo call inutile su array vuoto. Ora invocato nel
 *   subscribe.next dopo aver settato `this.box`.
 *
 *   • dropped swal `.swal2-container` querySelector + manual
 *     zIndex hack (workaround per modali nested non più
 *     necessario con pattern Diakonix z-index hierarchy),
 *
 *   • 6 subscribe annidate + Promise.then → 3-source forkJoin
 *     con `takeUntilDestroyed` (memory leak fix),
 *
 *   • switch a `inject()` + `ChangeDetectorRef.detectChanges()`.
 *
 * Behaviour preserved 1:1: payload `addOrder` per i sub-modali,
 * `deleteError` endpoint con Swal success/error feedback,
 * `calculatePreparationTime` formula (parsing date safe).
 */
@Component({
  selector: 'app-storico-ordini-dettagli',
  templateUrl: './storico-ordini-dettagli.component.html',
  styleUrls: ['./storico-ordini-dettagli.component.scss'],
  standalone: false,
})


export class StoricoOrdiniDettagliComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly ordersServ = inject(OrdersService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs ────────────────────────────────────────────────────────
  @Input() opened = false;
  @Input() order_id = '-1';

  // ── Data ──────────────────────────────────────────────────────────
  public order: Orders.IOrder | null = null;
  public documents: Documents.Document[] = [];
  public errorsArray: OrderErrors[] = [];
  public groupedBoxes: { ts_box_type_desc: string; quantity: number }[] = [];

  // ── Computed strings ──────────────────────────────────────────────
  public formattedDate = '—';
  public formattedClosing = '—';
  public preparationTime = '—';
  public isLinkToDDT = false;
  public linkToDDT: string | null = null;
  public linkCarrier: string | null = null;
  public linkAllLabels: string | null = null;
  public linkBordero: string | null = null;
  public shipment_id: string | number = '';


  // ── UI state ──────────────────────────────────────────────────────
  public loading = false;
  public openSection: 'tracking' | 'products' | 'boxes' | 'docs' | 'errors' | null =
    'products';

  /** Errore correntemente selezionato per la modifica (passato a H.4). */
  public errorToModify: OrderErrors | null = null;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadAll();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  /**
   * Carica order + documents (server + cache) + errors in parallelo.
   * Cache-wins semantics su documents preservata (convenzione legacy).
   */
  loadAll(): void {
    this.loading = true;
    const idNum = +this.order_id;

    forkJoin({
      order: this.ordersServ.getOrder1(idNum).pipe(
        catchError(() => of(null)),
      ),
      serverDocs: this.ordersServ.getDocuments(idNum).pipe(
        catchError(() => of([] as Documents.Document[])),
      ),
      cachedDocs: from(this.ordersServ.getOrderDocuments(this.order_id)).pipe(
        catchError(() => of(null)),
      ),
      errors: this.ordersServ.getOrderErrors(idNum).pipe(
        catchError(() => of([] as OrderErrors[])),
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ order, serverDocs, cachedDocs, errors }) => {
          this.order = order ?? null;
          this.documents = cachedDocs ?? serverDocs ?? [];
          this.errorsArray = errors ?? [];

          if (this.order) {
            // FIX BUG: groupBoxes ora chiamato DOPO che this.box è
            // popolato (il legacy lo chiamava in ngOnInit prima del
            // subscribe — primo call su array vuoto).
            this.groupedBoxes = this.computeGroupedBoxes(
              this.order.boxes ?? [],
            );

            this.formattedDate = this.formatOrEmpty(
              this.order.or_assignment_date,
            );
            this.formattedClosing = this.formatOrEmpty(
              this.order.or_closing_date,
            );
            this.preparationTime = this.calculatePreparationTime(this.order);

            // Etichette PDF links
            this.isLinkToDDT =
              (this.order.or_url_ddt?.indexOf('.pdf') ?? -1) > 0;
            this.linkToDDT = this.isLinkToDDT ? this.order.or_url_ddt : null;
            this.linkCarrier =
              this.order.shipment?.os_label_url?.[0]?.sm_label_link ?? null;
            this.linkAllLabels = this.order.or_url_boxes_label ?? null;

            // 🚨 Fix 2026-07-08: pre-fix il linkBordero era `this.order.
            // or_url_box_label` — campo BE con path minuscolo malformato
            // (`/repository/docs/bordero/4/`) che dà 404 in prod.
            // Il vero PDF link è `bo_pdf_link` dal record `OrderBorderos`
            // associato via `order_number`. Fetchiamo la lista borderò
            // e joiniamo. Se manca match (bordero non ancora generato),
            // linkBordero resta null e il template non mostra il link.
            this.linkBordero = null;
            this.resolveBorderoLink();
          }

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Risolve il link PDF del borderò associato all'ordine corrente.
   *
   * Il campo BE `or_url_box_label` sull'oggetto ordine restituisce un
   * path malformato (`/repository/docs/bordero/<id>/`, minuscolo, senza
   * filename) che dà 404 in prod. Il vero URL è in `bo_pdf_link` del
   * record `OrderBorderos` (endpoint `getNewBorderos`), che è quello
   * usato dalla pagina Borderò list. Qui joiniamo per `order_number`.
   *
   * Costo: 1 fetch per ogni apertura del dettaglio ordine. Se in futuro
   * diventa hotspot, il fix pulito è un endpoint BE dedicato
   * `/api/bordero/by-order/:orderNumber/` — ticket a parte.
   */
  private resolveBorderoLink(): void {
    if (!this.order?.or_order_number) return;
    const targetOrderNum = +this.order.or_order_number;
    this.ordersServ
      .getNewBorderos()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          const borderos = (result?.data ?? []) as Orders.OrderBorderos[];
          const match = borderos.find((b) => +b.order_number === targetOrderNum);
          this.linkBordero = match?.bo_pdf_link ?? null;
          this.cdr.detectChanges();
        },
        error: () => {
          // Fetch fallita — link resta null. Template gestisce già il
          // caso con `@if (linkBordero)`.
        },
      });
  }

  /** Reload solo della lista errori (chiamato dopo add/modify/delete). */
  reloadErrors(): void {
    this.ordersServ
      .getOrderErrors(+this.order_id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of([] as OrderErrors[])),
      )
      .subscribe((data) => {
        this.errorsArray = data ?? [];
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Pure helpers.
  // ──────────────────────────────────────────────────────────────────

  /** True se la data è il sentinel BE per "vuoto" (0001-01-01). */
  isClosingEmpty(value: string | null | undefined): boolean {
    return !value || String(value).startsWith('0001-01-01');
  }

  private formatOrEmpty(value: string | null | undefined): string {
    if (!value || this.isClosingEmpty(value)) return '—';
    try {
      return formatDate(value, 'dd/MM/yyyy', 'it');
    } catch {
      return '—';
    }
  }

  private computeGroupedBoxes(
    boxes: Orders.IOrder['boxes'],
  ): { ts_box_type_desc: string; quantity: number }[] {
    const map = new Map<string, number>();
    for (const b of boxes) {
      const key = b.ts_box_type_desc ?? '—';
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([ts_box_type_desc, quantity]) => ({
      ts_box_type_desc,
      quantity,
    }));
  }

  /**
   * Calcola tempo preparazione fra assignment_date e closing_date.
   * Parsing date safe (Date constructor invece di regex split fragile).
   */
  private calculatePreparationTime(order: Orders.IOrder): string {
    if (!order?.or_assignment_date || !order?.or_closing_date) {
      return 'Dati mancanti';
    }
    if (this.isClosingEmpty(order.or_closing_date)) {
      return 'In corso';
    }

    const start = new Date(order.or_assignment_date);
    const end = new Date(order.or_closing_date);
    const diff = end.getTime() - start.getTime();

    if (!Number.isFinite(diff) || diff < 0) {
      return 'Errore nelle date';
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    const daysStr = days === 1 ? '1 giorno' : `${days} giorni`;
    const hoursStr = hours === 1 ? '1 ora' : `${hours} ore`;
    const minutesStr = minutes === 1 ? '1 minuto' : `${minutes} minuti`;
    return `${daysStr}, ${hoursStr}, ${minutesStr}`;
  }

  isPartial(): boolean {
    return this.order?.or_partial === 'S';
  }

  // ──────────────────────────────────────────────────────────────────
  // Accordion (single-open).
  // ──────────────────────────────────────────────────────────────────

  toggleSection(name: 'tracking' | 'products' | 'boxes' | 'docs' | 'errors'): void {
    this.openSection = this.openSection === name ? null : name;
  }

  // ──────────────────────────────────────────────────────────────────
  // Errori sub-modal actions.
  // ──────────────────────────────────────────────────────────────────

  addError(): void {
    this.closeServ.openErrorModal();
  }

  enableModifyError(error: OrderErrors): void {
    this.errorToModify = error;
    this.closeServ.openModifyError();
  }

  deleteError(error_order_id: number): void {
    if (!error_order_id) return;

    Swal.fire({
      title: 'Eliminare errore?',
      text: 'L\'errore verrà rimosso permanentemente.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sì, elimina',
      cancelButtonText: 'Annulla',
      customClass: { popup: 'swal-storico-modal' },
    }).then((result) => {
      if (!result.isConfirmed) return;

      this.ordersServ
        .deleteError(error_order_id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: any) => {
            if (data) {
              Swal.fire({
                title: 'Eliminato!',
                text: 'Errore eliminato con successo.',
                icon: 'success',
                customClass: { popup: 'swal-storico-modal' },
                timer: 1500,
                timerProgressBar: true,
              });
              this.reloadErrors();
            } 
            else {
              Swal.fire({
                title: 'Non eliminato',
                text: 'Errore non eliminato, riprova.',
                icon: 'error',
                customClass: { popup: 'swal-storico-modal' },
                timer: 1500,
                timerProgressBar: true,
              });
            }
          },
          error: () => {
            Swal.fire({
              title: 'Non eliminato',
              text: 'Errore di rete, riprova.',
              icon: 'error',
              customClass: { popup: 'swal-storico-modal' },
              timer: 1500,
              timerProgressBar: true,
            });
          },
        });
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.closeServ.offStorico();
  }

  closeTracking(): void {
    this.closeServ.offOrderTrack();
  }
  
}
